import React, {useMemo, useState} from 'react';
import {diApi} from '@di-shared/api/di';
import Tab from '@di-shared/features/common/Tab';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  MileageChallengeDescError,
  MileageChallengeDescUpdate,
  MileageChallengeDescV2,
} from '@di-shared/features/mileage/ecomileage-v2/MileageChallengeDescV2';
import {
  MileageChallengeErrorGraph,
  MileageChallengeFailGraph,
  MileageChallengeTab,
  MileageChallengeType1Graph,
  MileageChallengeType2Graph,
  MileageChallengeType3Graph,
  MileageChallengeType4Graph,
  MileageChallengeType5Graph,
  MileageChallengeUpdateGraph,
} from '@di-shared/features/mileage/ecomileage-v2/MileageChallengeGraphV2';
import MidwayPerformanceRegistration from '@di-shared/features/mileage/ecomileage/MidwayPerformanceRegistration';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {getDDayString} from '@di-shared/utils/dDay';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {differenceInDay, parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface MileageChallengeProps {
  vin: string;
}

const challengeTypeKeys = ['01', '02', '03', '04', '05', '06'] as const;
type ChallengeType = (typeof challengeTypeKeys)[number];
type ChallengeDateType = Record<ChallengeType, {mileage: number; limitDistance: number}>;

function MileageChallengeV2({vin}: MileageChallengeProps) {
  const {odometer, updatedDate} = useOdometer(vin);
  const {data: calcLinkData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.ecoMileageController.calcLink), vin],
    queryFn: () => diApi.ecoMileageController.calcLink({vin}),
  });
  const challengeData = useMemo<ChallengeDateType>(
    () => ({
      // type1의 마일리지, type1의 제한주행거리
      '01': {
        mileage: calcLinkData.data.rtn_data.type1_mileage,
        limitDistance: calcLinkData.data.rtn_data.type1_dstnc,
      },
      // type2의 마일리지, type2의 제한주행거리
      '02': {
        mileage: calcLinkData.data.rtn_data.type2_mileage,
        limitDistance: calcLinkData.data.rtn_data.type2_dstnc,
      },
      '03': {
        mileage: calcLinkData.data.rtn_data.type3_mileage,
        limitDistance: calcLinkData.data.rtn_data.type3_dstnc,
      },
      '04': {
        mileage: calcLinkData.data.rtn_data.type4_mileage,
        limitDistance: calcLinkData.data.rtn_data.type4_dstnc,
      },
      '05': {
        mileage: calcLinkData.data.rtn_data.type5_mileage,
        limitDistance: calcLinkData.data.rtn_data.type5_dstnc,
      },
      // 06: 실패 케이스
      '06': {
        mileage: 0,
        limitDistance: 0,
      },
    }),
    [calcLinkData.data]
  );

  const challengeType = calcLinkData.data.rtn_data.challenge_type as ChallengeType;

  /**
   * [2026-01-12:Jeyoung Lee] FIXME: type 지정
   */
  const challengeStartDate = parseDateWithFormat(calcLinkData.data.rtn_data.challenge_start_date) as Date;
  const challengeEndDate = parseDateWithFormat(calcLinkData.data.rtn_data.challenge_end_date) as Date;
  const challengeDDay = differenceInDay(new Date(), challengeEndDate);

  return (
    <>
      {(() => {
        switch (challengeType) {
          case '01':
            return (
              <MileageChallengeType1
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          case '02':
            return (
              <MileageChallengeType2
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          case '03':
            return (
              <MileageChallengeType3
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          case '04':
            return (
              <MileageChallengeType4
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          case '05':
            return (
              <MileageChallengeType5
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          case '06':
            return (
              <MileageChallengeFail
                vin={vin}
                challengeStartDate={challengeStartDate}
                challengeEndDate={challengeEndDate}
                challengeBoundary={challengeData}
                odometer={odometer}
                challengeDDay={challengeDDay}
                updatedDate={updatedDate}
              />
            );
          default:
            return <MileageChallengeError />;
        }
      })()}
    </>
  );
}

export default MileageChallengeV2;

export interface MileageChallengeBaseProps {
  vin: string;
  challengeStartDate: Date;
  challengeEndDate: Date;
  challengeBoundary: ChallengeDateType;
  odometer: number;
  challengeDDay: number;
  updatedDate: Date | null;
}

function getMileageDataByTab(challengeBoundary: ChallengeDateType, mileageChallengeTab: MileageChallengeTab) {
  switch (mileageChallengeTab) {
    case MileageChallengeTab['type1']:
      return challengeBoundary['01'];
    case MileageChallengeTab['type2']:
      return challengeBoundary['02'];
    case MileageChallengeTab['type3']:
      return challengeBoundary['03'];
    case MileageChallengeTab['type4']:
      return challengeBoundary['04'];
    case MileageChallengeTab['type5']:
    default:
      return challengeBoundary['05'];
  }
}

interface MileageChallingingTitleProps {
  challengeType: ChallengeType;
  challengeStartDate: Date | null;
  challengeEndDate: Date | null;
  challengeDDay: number;
  challengeData: ChallengeDateType;
}

function MileageChallingingTitle({
  challengeType,
  challengeStartDate,
  challengeEndDate,
  challengeDDay,
  challengeData,
}: MileageChallingingTitleProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <div className="section_heading type_main">
      <h3
        className="section_heading_title"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T30228_$mileage', {mileage: challengeData[challengeType].mileage / 10000})}
        <span className="badge badge_basic_container badge_25px">{getDDayString(challengeDDay)}</span>
      </h3>
      {challengeStartDate && challengeEndDate && (
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      )}
    </div>
  );
}

function MileageChallengeType1({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type1']);

  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
  ];

  return (
    <>
      <MileageChallingingTitle
        challengeType="01"
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeDDay={challengeDDay}
        challengeData={challengeBoundary}
      />
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeType1Graph
        odometer={odometer}
        limitDistance={challengeBoundary['01'].limitDistance}
        previousLimitDistance={0}
        tabLimitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeType2({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type2']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
  ];
  return (
    <>
      <MileageChallingingTitle
        challengeType="02"
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeDDay={challengeDDay}
        challengeData={challengeBoundary}
      />
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeType2Graph
        odometer={odometer}
        limitDistance={challengeBoundary['02'].limitDistance}
        previousLimitDistance={challengeBoundary['01'].limitDistance}
        tabLimitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeType3({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type3']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
  ];
  return (
    <>
      <MileageChallingingTitle
        challengeType="03"
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeDDay={challengeDDay}
        challengeData={challengeBoundary}
      />
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeType3Graph
        odometer={odometer}
        limitDistance={challengeBoundary['03'].limitDistance}
        previousLimitDistance={challengeBoundary['02'].limitDistance}
        tabLimitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeType4({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type4']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
  ];
  return (
    <>
      <MileageChallingingTitle
        challengeType="04"
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeDDay={challengeDDay}
        challengeData={challengeBoundary}
      />
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeType4Graph
        odometer={odometer}
        limitDistance={challengeBoundary['04'].limitDistance}
        previousLimitDistance={challengeBoundary['03'].limitDistance}
        tabLimitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeType5({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type5']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
  ];
  return (
    <>
      <MileageChallingingTitle
        challengeType="05"
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeDDay={challengeDDay}
        challengeData={challengeBoundary}
      />
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeType5Graph
        odometer={odometer}
        limitDistance={challengeBoundary['05'].limitDistance}
        previousLimitDistance={challengeBoundary['04'].limitDistance}
        tabLimitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        mileageChallengeTab={mileageChallengeTab}
      />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

function MileageChallengeFail({
  challengeStartDate,
  challengeEndDate,
  challengeBoundary,
  odometer,
  challengeDDay,
  updatedDate,
}: MileageChallengeBaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [mileageChallengeTab, setMileageChallengeTab] = useState(MileageChallengeTab['type1']);
  const chips = [
    {text: t('T10125_$mileage', {mileage: challengeBoundary['01'].mileage / 10000}), tab: MileageChallengeTab['type1']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['02'].mileage / 10000}), tab: MileageChallengeTab['type2']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['03'].mileage / 10000}), tab: MileageChallengeTab['type3']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['04'].mileage / 10000}), tab: MileageChallengeTab['type4']},
    {text: t('T10125_$mileage', {mileage: challengeBoundary['05'].mileage / 10000}), tab: MileageChallengeTab['type5']},
  ];
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30229')}
          <span className="badge badge_25px badge_filled_container badge_container_gray">{getDDayString(challengeDDay)}</span>
        </h3>
        <p className="section_heading_desc">
          {t('T30230_$startDate_$endDate', {
            startDate: CldrDatetimeFormatter.format(challengeStartDate, 'yMMMd'),
            endDate: CldrDatetimeFormatter.format(challengeEndDate, 'yMMMd'),
          })}
        </p>
      </div>
      <Tab
        element="div"
        className="chips"
        role="tablist"
        selectedValue={mileageChallengeTab.toString()}
      >
        {chips.map(({text, tab}) => (
          <Tab.Item
            key={tab}
            value={tab.toString()}
            element="button"
            activeClassName="is-selected"
            className="chip"
            onClick={() => setMileageChallengeTab(tab)}
          >
            <span className="body_small_r">{text}</span>
          </Tab.Item>
        ))}
      </Tab>
      <MileageChallengeFailGraph mileageChallengeTab={mileageChallengeTab} />
      <MileageChallengeDescV2
        mileage={getMileageDataByTab(challengeBoundary, mileageChallengeTab).mileage}
        odometer={odometer}
        limitDistance={getMileageDataByTab(challengeBoundary, mileageChallengeTab).limitDistance}
        challengeEndDate={challengeEndDate}
        updatedDate={updatedDate}
      />
    </>
  );
}

interface MileageChallengeUpdateV2Props {
  vin: string;
  odometer: number;
  updatedDate: Date | null;
}

export function MileageChallengeUpdateV2({vin, odometer, updatedDate}: MileageChallengeUpdateV2Props) {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30247')}
        </h3>
        <p className="section_heading_desc">
          {t('T30359')}:&nbsp;{t('T30360')}
        </p>
      </div>
      <MileageChallengeUpdateGraph />
      <MidwayPerformanceRegistration vin={vin} />
      <MileageChallengeDescUpdate
        odometer={odometer}
        updatedDate={updatedDate}
      />
    </>
  );
}

export function MileageChallengeError() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_heading type_main">
        <h3 className="section_heading_title">{t('T30249')}</h3>
      </div>
      <MileageChallengeErrorGraph />
      <MileageChallengeDescError />
    </>
  );
}
